import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { ImageZoomModal } from '@/components/order/ImageZoomModal';
import { UkrposhtaTrackingDialog } from '@/components/order/UkrposhtaTrackingDialog';
import { UkrposhtaShipmentSummaryDialog } from '@/components/admin/UkrposhtaShipmentSummaryDialog';
import AdminOrdersWorkspace, {
  type AdminWorkspaceMode,
  type AdminWorkspacePreviewMeta,
} from '@/components/admin/AdminOrdersWorkspace';
import ManualTrackingDialog from '@/components/admin/ordersWorkspace/ManualTrackingDialog';
import { useAdminOrderItemsData } from '@/components/admin/ordersWorkspace/useAdminOrderItemsData';
import { useAdminOrdersData } from '@/components/admin/ordersWorkspace/useAdminOrdersData';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { AdminWorkspaceCurrencyCode } from '@/components/admin/ordersWorkspace/types';
import { fetchAdminExchangeRates } from '@/services/adminService';
import { fetchSupportedCurrencies, selectCurrencyStatus } from '@/features/reference/referenceSlice';
import { selectMeasurementUnit, selectSelectedCurrency, setMeasurementUnit, setSelectedCurrency } from '@/features/preferences/preferencesSlice';

type AdminOrderItemsManagementPageProps = {
  initialMode?: AdminWorkspaceMode;
};

const ADMIN_WORKSPACE_MODE_STORAGE_KEY = 'tulasi.adminOrdersWorkspace.mode';

const readStoredAdminWorkspaceMode = (initialMode: AdminWorkspaceMode): AdminWorkspaceMode => {
  if (typeof window === 'undefined') {
    return initialMode;
  }

  const stored = window.localStorage.getItem(ADMIN_WORKSPACE_MODE_STORAGE_KEY);
  return stored === 'orders' || stored === 'items' ? stored : initialMode;
};

const AdminOrderItemsManagementPage = ({ initialMode = 'items' }: AdminOrderItemsManagementPageProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const isAdmin = user?.role === 'ADMIN';
  const measurementUnit = useAppSelector(selectMeasurementUnit);
  const selectedCurrency = useAppSelector(selectSelectedCurrency);
  const currencyStatus = useAppSelector(selectCurrencyStatus);

  const [mode, setMode] = useState<AdminWorkspaceMode>(() => readStoredAdminWorkspaceMode(initialMode));
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewMeta, setPreviewMeta] = useState<AdminWorkspacePreviewMeta | null>(null);
  const [usdToUahRate, setUsdToUahRate] = useState<number | null>(null);

  const currencyCode = useMemo<AdminWorkspaceCurrencyCode>(() => (selectedCurrency === 'UAH' ? 'UAH' : 'USD'), [selectedCurrency]);

  const closePreview = useCallback(() => {
    setIsPreviewOpen(false);
    setPreviewUrl(null);
    setPreviewMeta(null);
  }, []);

  const openPreview = useCallback((url: string | null, meta?: AdminWorkspacePreviewMeta | null) => {
    if (!url) {
      return;
    }
    setPreviewUrl(url);
    setPreviewMeta(meta ?? null);
    setIsPreviewOpen(true);
  }, []);

  useEffect(() => {
    if (currencyStatus === 'idle') {
      dispatch(fetchSupportedCurrencies());
    }
  }, [currencyStatus, dispatch]);

  useEffect(() => {
    if (!isAdmin) {
      return;
    }

    let cancelled = false;
    void fetchAdminExchangeRates({ baseCurrency: 'USD', quoteCurrency: 'UAH', limit: 1 })
      .then((rates) => {
        if (cancelled) {
          return;
        }
        const rate = rates[0]?.rate;
        setUsdToUahRate(typeof rate === 'number' && Number.isFinite(rate) && rate > 0 ? rate : null);
      })
      .catch(() => {
        if (!cancelled) {
          setUsdToUahRate(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  const refreshOrdersRef = useRef<() => void>(() => undefined);
  const refreshItemsRef = useRef<() => void>(() => undefined);

  const items = useAdminOrderItemsData(isAdmin, () => refreshOrdersRef.current(), measurementUnit);
  const orders = useAdminOrdersData(isAdmin, () => refreshItemsRef.current());

  useEffect(() => {
    refreshOrdersRef.current = () => void orders.actions.loadOrders();
    refreshItemsRef.current = () => void items.actions.loadItems();
  }, [items.actions, orders.actions]);

  const openOrderMode = useCallback((displayCode: string) => {
    setMode('orders');
    orders.actions.setSearchInput(displayCode);
  }, [orders.actions]);

  const openItemsMode = useCallback((displayCode: string) => {
    setMode('items');
    items.actions.setSearchInput(displayCode);
  }, [items.actions]);

  const handleSharedDateFromChange = useCallback((value: string) => {
    items.actions.setDateFrom(value);
    orders.actions.setDateFrom(value);
  }, [items.actions, orders.actions]);

  const handleSharedDateToChange = useCallback((value: string) => {
    items.actions.setDateTo(value);
    orders.actions.setDateTo(value);
  }, [items.actions, orders.actions]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(ADMIN_WORKSPACE_MODE_STORAGE_KEY, mode);
  }, [mode]);

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12">
        <Card>
          <CardHeader>
            <CardTitle>{t('adminOrdersWorkspace.page.accessRequiredTitle', 'Admin access required')}</CardTitle>
            <CardDescription>{t('adminOrdersWorkspace.page.accessRequiredDescription', 'You do not have permission to view this page.')}</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button onClick={() => navigate('/')}>{t('adminOrdersWorkspace.page.returnHome', 'Return home')}</Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 space-y-8">
      <ManualTrackingDialog
        open={orders.state.manualTrackingOrder != null}
        form={orders.state.manualTrackingForm}
        status={orders.state.manualTrackingStatus}
        error={orders.state.manualTrackingError}
        onOpenChange={(open) => {
          if (!open) {
            orders.actions.closeManualTrackingDialog();
          }
        }}
        onFormChange={orders.actions.setManualTrackingForm}
        onSave={() => void orders.actions.handleManualTrackingSave()}
      />

      <UkrposhtaTrackingDialog
        open={orders.state.trackingOrderId != null}
        orderId={orders.state.trackingOrderId}
        onClose={() => orders.actions.setTrackingOrderId(null)}
      />
      <UkrposhtaShipmentSummaryDialog
        open={orders.state.selectedUkrposhtaOrder != null}
        order={orders.state.selectedUkrposhtaOrder}
        onClose={() => orders.actions.setSelectedUkrposhtaOrder(null)}
        currencyCode={currencyCode}
        usdToUahRate={usdToUahRate}
      />

      <AdminOrdersWorkspace
        mode={mode}
        onModeChange={setMode}
        measurementUnit={measurementUnit}
        onMeasurementUnitChange={(unit) => dispatch(setMeasurementUnit(unit))}
        currencyCode={currencyCode}
        onCurrencyCodeChange={(nextCurrency) => dispatch(setSelectedCurrency(nextCurrency))}
        usdToUahRate={usdToUahRate}
        itemState={items.state}
        orderState={{
          status: orders.state.status,
          error: orders.state.error,
          orders: orders.state.orders,
          groupedOrders: orders.state.groupedOrders,
          statusOptions: orders.state.statusOptions,
          visibleStatuses: orders.state.visibleStatuses,
          orderStatusSavingId: orders.state.orderStatusSavingId,
          searchInput: orders.state.searchInput,
          dateFrom: orders.state.dateFrom,
          dateTo: orders.state.dateTo,
        }}
        onRefreshItems={() => void items.actions.loadItems()}
        onRefreshOrders={() => void orders.actions.loadOrders()}
        onResetFilters={() => {
          items.actions.resetFilters();
          orders.actions.resetFilters();
        }}
        onItemGroupModeChange={items.actions.setGroupMode}
        onItemSearchInputChange={items.actions.setSearchInput}
        onItemDateFromChange={handleSharedDateFromChange}
        onItemDateToChange={handleSharedDateToChange}
        onItemOnlyFirstOrderChange={items.actions.setOnlyFirstOrder}
        onToggleItemStatusFilter={items.actions.toggleStatusFilter}
        onExportAllItems={() => void items.actions.handleExportAll()}
        onExportItemGroup={(groupKey, groupItems, targetDpi) => void items.actions.handleExportGroup(groupKey, groupItems, targetDpi)}
        onExportItemGroupDpiChange={items.actions.setGroupExportDpi}
        onItemStatusChange={(orderItemId, statusValue) => void items.actions.handleItemStatusChange(orderItemId, statusValue)}
        onDownloadItemWithMargin={(item) => void items.actions.handleDownloadWithMargin(item)}
        reuploadDialogItem={items.state.reuploadDialogItem}
        reuploadComment={items.state.reuploadComment}
        reuploadSubmitting={items.state.reuploadSubmitting}
        onReuploadCommentChange={items.actions.setReuploadComment}
        onReuploadDialogConfirm={() => void items.actions.confirmReuploadDialog()}
        onReuploadDialogOpenChange={(open) => { if (!open) { items.actions.closeReuploadDialog(); } }}
        onOpenPreview={openPreview}
        onOpenOrder={openOrderMode}
        onOpenOrderItems={openItemsMode}
        onOrderSearchInputChange={orders.actions.setSearchInput}
        onOrderDateFromChange={handleSharedDateFromChange}
        onOrderDateToChange={handleSharedDateToChange}
        onToggleOrderStatusVisibility={orders.actions.toggleOrderStatusVisibility}
        onOrderGroupPrintLabels={orders.actions.handleOrderGroupPrintLabels}
        onOrderStatusChange={(orderId, statusValue) => void orders.actions.handleOrderStatusChange(orderId, statusValue)}
        onOrderAction={(action, orderId) => void orders.actions.handleOrderAction(action, orderId)}
      />

      {previewUrl ? (
        <ImageZoomModal
          isOpen={isPreviewOpen}
          onClose={closePreview}
          imageUrl={previewUrl}
          widthIn={previewMeta?.widthIn ?? null}
          heightIn={previewMeta?.heightIn ?? null}
          marginIn={previewMeta?.marginIn ?? null}
          originalWidthPx={previewMeta?.originalWidthPx ?? null}
          originalHeightPx={previewMeta?.originalHeightPx ?? null}
        />
      ) : null}
    </div>
  );
};

export default AdminOrderItemsManagementPage;
